/**
 * Deep-link helpers for the login round-trip.
 *
 * Both functions were previously inline closures — `ProtectedRoute` in
 * App.js built the `?next=` parameter, and `LoginPage.getPostLoginDest`
 * consumed it. Neither could be tested without rendering the whole app,
 * so the behaviour they encode (including an open-redirect guard) had no
 * regression cover at all.
 *
 * They are extracted here verbatim — same inputs, same outputs — so the
 * route-fallback work of 2026-09-14 can prove it did not disturb the
 * deep-link auth return path.
 */

/**
 * Where to send an unauthenticated visitor who asked for a protected
 * path, preserving their original destination for after they sign in.
 *
 * @param {string} pathname - location.pathname
 * @param {string} [search] - location.search (may be "" or undefined)
 * @returns {string} a path to <Navigate to={...} replace />
 */
export function buildLoginRedirect(pathname, search) {
  const target = `${pathname}${search || ""}`;
  const isAlreadyRoot = pathname === "/" || pathname === "";
  const next = isAlreadyRoot ? "" : `?next=${encodeURIComponent(target)}`;
  return `/${next}`;
}

/**
 * Where to land a user after a successful sign-in.
 *
 * Only same-origin absolute paths are honoured. A protocol-relative
 * `//attacker.com` or a fully-qualified `https://attacker.com` must never
 * be followed — that would turn the login page into an open redirect.
 *
 * @param {string} [search] - location.search containing `?next=`
 * @returns {string} the post-login destination
 */
export function safePostLoginDest(search) {
  try {
    const params = new URLSearchParams(search || "");
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      return next;
    }
  } catch { /* fall through */ }
  return "/dashboard";
}
